import React, { useEffect, useState, useMemo } from "react";
import DynamicTable from "../common/DynamicTable";
import SubTitle from "../common/SubTitle";
import { CARD_BACKGROUND_COLORS, TEXT_COLORS } from "../../constants/styles";
import Cards from "../common/Cards";
import SearchFilter from "../common/SearchFilter";
import { ACTIONS_COLUMN_ID } from "../../constants/dashboardConstants";
import { useNavigate } from "react-router-dom";
import { getAllTrackingDevices, deleteTrackingDevice } from "./deviceApi";
import Update_Icon from "../../images/table/Update_icon.svg";
import Delete_Icon from "../../images/table/Delete_icon.svg";
import DeleteModal from "../common/DeleteModal";
import { Box } from "@mui/material";

// Define custom actions for the table
const customActions = [
  {
    id: "update",
    label: "Update",
    icon: Update_Icon,
    path: "/device/edit",
  },
  {
    id: "delete",
    label: "Delete",
    className: "delete",
    icon: Delete_Icon,
    path: "",
  },
];

export default function DeviceList() {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({});

  const [filterConfig, setFilterConfig] = useState([
    {
      id: "status",
      label: "Status",
      helperText: "Select device status",
      options: [
        { value: "INSTALLED", label: "Installed" },
        { value: "PENDING", label: "Not Installed" },
      ],
    },
    {
      id: "condition",
      label: "Condition",
      helperText: "Select working condition",
      options: [
        { value: "WORKING", label: "Working" },
        { value: "NOT_WORKING", label: "Not Working" },
        { value: "NEEDS_REPAIR", label: "Needs Repair" },
      ],
    },
    {
      id: "organization",
      label: "Organization",
      helperText: "Select organization",
      options: [],
    },
  ]);

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        console.log("Fetching devices...");
        const response = await getAllTrackingDevices();
        console.log("Raw API response:", response);

        const deviceData = response?.data || [];
        console.log("Device data:", deviceData);

        const safeDeviceData = Array.isArray(deviceData) ? deviceData : [];
        setDevices(safeDeviceData);

        // Extract unique organizations for filter - similar to Vehicle component's route extraction
        const uniqueOrganizations = [];
        const orgIds = new Set();

        safeDeviceData.forEach((device) => {
          const orgId = device?.vehicle?.organization?.id;
          const orgName = device?.vehicle?.organization?.name;

          if (orgId && orgName && !orgIds.has(orgId)) {
            orgIds.add(orgId);
            uniqueOrganizations.push({
              value: orgId.toString(),
              label: orgName,
            });
          }
        });

        // Update filter config with organizations
        setFilterConfig((prev) => {
          const updated = [...prev];
          const orgIndex = updated.findIndex((f) => f.id === "organization");
          if (orgIndex >= 0) {
            updated[orgIndex].options = uniqueOrganizations;
          }
          return updated;
        });
      } catch (error) {
        console.error("Error fetching devices:", error);
        console.error("Error details:", error.message);
        console.error("Error stack:", error.stack);
        setDevices([]);
      }
    };

    fetchDevices();
  }, []);

  const headers = [
    { key: "deviceId", value: "Device ID" },
    { key: "imeiNumber", value: "IMEI No." },
    { key: "workingCondition", value: "Condition" },
    { key: "status", value: "Status" },
    { key: "simCardId", value: "SIM No." },
    { key: "simCard.expiryDate", value: "Next Recharge" },
    { key: "organization", value: "Organization" },
    { key: "vehicles", value: "Vehicle" },
    { key: ACTIONS_COLUMN_ID, value: "Actions" },
  ];

  const formatWorkingCondition = (condition) => {
    switch (condition) {
      case "WORKING":
        return "Working";
      case "NOT_WORKING":
        return "Not Working";
      case "NEEDS_REPAIR":
        return "Needs Repair";
      default:
        return "Unknown";
    }
  };

  const formatStatus = (status) => {
    switch (status) {
      case "INSTALLED":
        return "Installed";
      case "PENDING":
        return "Not Installed";
      default:
        return "-";
    }
  };

  // CRITICAL: Always ensure devices is an array - this prevents the crash
  const safeDevices = Array.isArray(devices) ? devices : [];

  const filteredDevices = useMemo(() => {
    return safeDevices.filter((device) => {
      // Filter logic similar to Vehicle component
      const statusMatch = !filters.status || device.status === filters.status;
      const conditionMatch =
        !filters.condition || device.workingCondition === filters.condition;

      // Organization filter match
      const orgId = device?.vehicle?.organization?.id;
      const organizationMatch =
        !filters.organization ||
        (orgId && String(orgId) === String(filters.organization));

      // If any filter doesn't match, exclude the device
      if (!statusMatch || !conditionMatch || !organizationMatch) {
        return false;
      }

      // If no search query, return true (show all that match filters)
      if (!searchTerm) {
        return true;
      }

      // Otherwise, search in all relevant fields
      const query = searchTerm.toLowerCase();
      return (
        (device.deviceId && device.deviceId.toLowerCase().includes(query)) ||
        (device.imeiNumber &&
          device.imeiNumber.toLowerCase().includes(query)) ||
        // FIXED: Search in formatted condition instead of raw value
        formatWorkingCondition(device.workingCondition)
          .toLowerCase()
          .includes(query) ||
        // FIXED: Search in formatted status instead of raw value
        formatStatus(device.status).toLowerCase().includes(query) ||
        (device.simCard?.simNumber &&
          device.simCard.simNumber.toLowerCase().includes(query)) ||
        (device.vehicle?.organization?.name &&
          device.vehicle.organization.name.toLowerCase().includes(query)) ||
        (device.vehicle?.name &&
          device.vehicle.name.toLowerCase().includes(query))
      );
    });
  }, [safeDevices, filters, searchTerm]);

  // Transform filtered devices to table format
  const filteredTableData = useMemo(() => {
    return filteredDevices.map((device) => {
      const simCard = device?.simCard || {};
      const vehicle = device?.vehicle;

      return {
        deviceId: device?.deviceId || "-",
        imeiNumber: device?.imeiNumber || "-",
        workingCondition: formatWorkingCondition(device?.workingCondition),
        status: formatStatus(device?.status),
        simCardId: simCard?.simNumber || "-",
        "simCard.expiryDate": simCard?.planExpiry
          ? new Date(simCard.planExpiry).toLocaleDateString("en-GB")
          : "-",
        organization: vehicle?.organization?.name || "-",
        vehicles: vehicle?.name || "-",
        id: device?.id,
        originalData: device,
      };
    });
  }, [filteredDevices]);

  // Handle search input change
  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  // Handle filter changes - similar to Vehicle component
  const handleFilterChange = (filterValues) => {
    setFilters(filterValues);
    console.log("Filter values:", filterValues);
  };

  // Handle action clicks
  const handleActionClick = (actionId, rowIndex, actionObj) => {
    console.log("Action clicked:", { actionId, rowIndex, actionObj });
    console.log("Filtered table data length:", filteredTableData.length);
    console.log("Full filtered table data:", filteredTableData);

    if (
      !Array.isArray(filteredTableData) ||
      rowIndex >= filteredTableData.length
    ) {
      console.error(
        "Invalid row index or table data:",
        rowIndex,
        filteredTableData.length
      );
      return;
    }

    const rowData = filteredTableData[rowIndex];
    console.log("Row data:", rowData);

    if (!rowData) {
      console.error("Row data is undefined for index:", rowIndex);
      return;
    }

    const deviceId = rowData.id;
    console.log("Device ID:", deviceId);

    switch (actionId) {
      case "update":
        console.log("Navigating to edit page for device:", deviceId);
        navigate(`/device/${deviceId}`);
        break;
      case "delete":
        console.log("Setting up delete for device:", deviceId);
        setDeviceToDelete({
          id: deviceId,
          name: rowData.originalData?.deviceId || "Unknown Device",
          data: rowData.originalData,
        });
        setShowDeleteModal(true);
        break;
      default:
        console.log("Unknown action:", actionId);
        break;
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deviceToDelete) {
      console.error("No device to delete");
      return;
    }

    console.log("Attempting to delete device:", deviceToDelete);
    setIsDeleting(true);

    try {
      const deleteResponse = await deleteTrackingDevice(deviceToDelete.id);
      console.log("Delete response:", deleteResponse);

      // Update the devices state by removing the deleted device
      setDevices((prevDevices) => {
        const filteredDevices = (
          Array.isArray(prevDevices) ? prevDevices : []
        ).filter((device) => device?.id !== deviceToDelete.id);
        console.log("Updated devices after delete:", filteredDevices);
        return filteredDevices;
      });

      setShowDeleteModal(false);
      setDeviceToDelete(null);
      console.log(`Device ${deviceToDelete.name} deleted successfully`);
    } catch (error) {
      console.error("Failed to delete device:", error);
      console.error("Delete error details:", error.message);
      console.error("Delete error response:", error.response?.data);
      alert(`Failed to delete device: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle delete cancellation
  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeviceToDelete(null);
  };

  // Safe localStorage access
  const getOrgName = () => {
    try {
      const userState = JSON.parse(localStorage.getItem("userState") || "{}");
      return userState?.organization?.name || "Organization";
    } catch (error) {
      console.warn("Error parsing localStorage:", error);
      return "Organization";
    }
  };

  const orgName = getOrgName();

  return (
    <>
      <SubTitle
        title="Devices"
        path={`${orgName} > Devices`}
        add="+ Create Device"
        addPath="/device/add"
        onAddClick={() => navigate("/device/add")}
      />
      <Cards
        cardData={[
          {
            title: "Total Devices",
            value: safeDevices.length,
            backgroundColor: CARD_BACKGROUND_COLORS.DARK_BLUE,
            titleColor: TEXT_COLORS.WHITE,
            valueColor: TEXT_COLORS.WHITE,
          },
          {
            title: "Installed Devices",
            value: safeDevices.filter((d) => d?.status === "INSTALLED").length,
            backgroundColor: CARD_BACKGROUND_COLORS.GREEN,
            titleColor: TEXT_COLORS.WHITE,
            valueColor: TEXT_COLORS.WHITE,
          },
          {
            title: "Working Devices",
            value: safeDevices.filter((d) => d?.workingCondition === "WORKING")
              .length,
            backgroundColor: CARD_BACKGROUND_COLORS.YELLOW,
            titleColor: TEXT_COLORS.BLACK,
            valueColor: TEXT_COLORS.BLACK,
          },
          {
            title: "Expiry in Next 60 Days",
            value: safeDevices.filter((d) => {
              try {
                if (!d?.simCard?.planExpiry) return false;

                // Normalize both dates to midnight
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const expiryDate = new Date(d.simCard.planExpiry);
                expiryDate.setHours(0, 0, 0, 0);

                const diffDays = (expiryDate - today) / (1000 * 60 * 60 * 24);

                return diffDays >= 0 && diffDays <= 60;
              } catch (error) {
                return false;
              }
            }).length,
            backgroundColor: CARD_BACKGROUND_COLORS.RED,
            titleColor: TEXT_COLORS.WHITE,
            valueColor: TEXT_COLORS.WHITE,
          },
        ]}
      />
      <div className="table_container">
        <SearchFilter
          filters={filterConfig}
          searchPlaceholder="Search devices"
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
        />
        <DynamicTable
          key={`table-${
            filteredTableData.length
          }-${searchTerm}-${JSON.stringify(filters)}`}
          headers={headers}
          data={filteredTableData}
          onActionClick={handleActionClick}
          actions={customActions}
        />
      </div>

      <Box sx={{ height: "40px" }}></Box>
      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Device!"
        message={`Are you sure you want to delete device "${deviceToDelete?.name}"? This action cannot be undone.`}
        cancelButtonText="Cancel"
        confirmButtonText={isDeleting ? "Deleting..." : "Delete"}
      />
    </>
  );
}
